import {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { RootState } from "@src/reducers/store";
import {
  updateModalsCloseDrawerModal,
  MachineInformationForRequestRemoteAccessDrawer,
} from "@src/reducers/your-reducer";
import { setAllMachinesWithGatewaysListData } from "@src/reducers/gateways-machines-slice";
import { postVPNRequestSupportSessionUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-post-request-support-session-use-case";
import { postVPNDenySupportSessionUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-post-deny-support-session-use-case";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import {
  MachinesListSupportSessionsStatusTypes,
  MachinesListSupportSessionsZitadelRoleTypes,
} from "@common/types/machines-list-types";
import { decodeIso8601DurationToTimePeriod } from "../../../../cloud-connect/utils/decode-iso-8601-duration-from-time-period";
import { machinesMergeSupportSessionsListData } from "@src/cloud-connect/utils/machines-merge-support-sessions-list-data";
import STRemoteSupportIcon from "@src/assets/svg/st-request-remote-access.svg";
import STRemoteSupportRequestedtIcon from "@src/assets/svg/st-request-remote-access-requested.svg";
import OTRemoteSupportRequestedtIcon from "@src/assets/svg/ot-remote-support-requested.svg";
import moment from "moment";
import { postVPNAcceptSupportSessionUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-post-accept-support-session-use-case";
import { postVPNAcceptSupportSessionDto } from "@src/cloud-connect/api/vpn-manager/vpn-post-accept-support-session";
import { buildRequestedDuration } from "@src/cloud-connect/vpn-manager/utils/build-requested-ios-duration";
import { postVPNCancelSupportSessionUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-post-cancel-support-session-use-case";
import { SessionVisibilityType } from "@src/types/remote-vnc-session";
import { useSSEUnavailable } from "@src/cloud-connect/sse/use-sse-unavailable";

export type DurationType = "minutes" | "hours";

export interface TextsType {
  [key: string]: string;
}

export interface DurationOptionsType {
  minutes: number[];
  hours: number[];
}

export interface RequestedDurationIsoType {
  amount: number[];
  time_unit: string[];
}

export const useMachinesRequestRemoteMachineAccessDrawerModalHook = () => {
  const dispatch = useDispatch();
  const isRemoteActionsDisabled = useSSEUnavailable("service-vpn-manager");

  const [requestMessage, setRequestMessage] = useState<string>("");
  const [durationValue, setDurationValue] = useState<number>(15);
  const [durationType, setDurationType] = useState<DurationType>("minutes");
  const [sessionVisibility, setSessionVisibility] =
    useState<SessionVisibilityType>("private");
  const [hasSTPermission, setHasSTPermission] = useState<boolean>(false);
  const [isEditingDuration, setIsEditingDuration] = useState<boolean>(false);
  const [oTRequestAccessButtonLoading, setOTRequestAccessButtonLoading] =
    useState<boolean>(false);
  const [sTRequestAccessButtonLoading, setSTRequestAccessButtonLoading] =
    useState<boolean>(false);
  const [confirmCancelLoading, setConfirmCancelLoading] =
    useState<boolean>(false);

  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const allMachinesListData = useSelector(
    (state: RootState) => state.gatewaysMachinesSlice.allMachinesListData,
    shallowEqual,
  );

  const selectedMachineInfo: MachineInformationForRequestRemoteAccessDrawer =
    useSelector(
      (state: RootState) =>
        state.yourSlice.modals.machinesRequestRemoteMachineAccessDrawerModal
          .machineInformation,
      shallowEqual,
    );

  const mergeSupportSessionResponse = useCallback(
    (supportSessionsPayload: unknown) => {
      const mergedData = machinesMergeSupportSessionsListData({
        allMachinesListData,
        supportSessionsPayload: supportSessionsPayload as never,
        isPostSupportSessionResponse: true,
      });
      if (mergedData) {
        dispatch(setAllMachinesWithGatewaysListData(mergedData));
      }
    },
    [allMachinesListData, dispatch],
  );

  const IsRequestedStatus = useMemo(
    () =>
      selectedMachineInfo?.status ===
      MachinesListSupportSessionsStatusTypes.REQUESTED,
    [selectedMachineInfo],
  );

  const durationOptions: DurationOptionsType = useMemo(
    () => ({ minutes: [15, 30, 45, 60], hours: [2, 3, 5, 8] }),
    [],
  );

  useEffect(() => {
    if (!authContext) return;

    const permissions = authContext.permissions;

    const hasManage = permissions.includes(
      MachinesListSupportSessionsZitadelRoleTypes.MANAGE_VPN,
    );
    const hasRequest = permissions.includes(
      MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN,
    );
    const hasAccept = permissions.includes(
      MachinesListSupportSessionsZitadelRoleTypes.ACCEPT_VPN,
    );

    const isRequester =
      selectedMachineInfo?.userType ===
      MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN;

    if (hasManage) {
      setHasSTPermission(isRequester);
      return;
    }

    if (hasRequest && hasAccept) {
      setHasSTPermission(isRequester);
      return;
    }

    if (hasRequest) {
      setHasSTPermission(true);
      return;
    }

    if (hasAccept) {
      setHasSTPermission(false);
      return;
    }
  }, [authContext, selectedMachineInfo?.userType]);

  useEffect(() => {
    if (IsRequestedStatus && selectedMachineInfo?.requested_duration) {
      const decodedDuration = decodeIso8601DurationToTimePeriod(
        selectedMachineInfo.requested_duration,
      );
      if (decodedDuration?.amount?.[0] && decodedDuration?.time_unit?.[0]) {
        setDurationValue(decodedDuration.amount[0]);
        setDurationType(
          decodedDuration.time_unit[0] === "minutes" ? "minutes" : "hours",
        );
      }
    }
  }, [IsRequestedStatus, selectedMachineInfo?.requested_duration]);

  const handleCloseModal = useCallback((): void => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "machinesRequestRemoteMachineAccessDrawerModal",
      }),
    );
  }, [dispatch]);

  const selectMinutes = useCallback(
    (e: MouseEvent | KeyboardEvent): void => {
      e.preventDefault();
      setDurationType("minutes");
      setDurationValue(durationOptions.minutes[0]);
    },
    [durationOptions],
  );

  const selectHours = useCallback(
    (e: MouseEvent | KeyboardEvent): void => {
      e.preventDefault();
      setDurationType("hours");
      setDurationValue(durationOptions.hours[0]);
    },
    [durationOptions],
  );

  const selectDurationValue = useCallback(
    (e: MouseEvent | KeyboardEvent, value: number): void => {
      e.preventDefault();
      setDurationValue(value);
      setIsEditingDuration(false);
    },
    [],
  );

  const onChangeRequestMessage = useCallback((value: string): void => {
    setRequestMessage(value);
  }, []);

  const selectSessionVisibility = (value: SessionVisibilityType): void => {
    setSessionVisibility(value);
  };

  const handleEditDuration = useCallback((): void => {
    setIsEditingDuration(true);
  }, []);
  const handleCloseEditDuration = useCallback((): void => {
    setIsEditingDuration(false);
  }, []);

  const handleSTRequestAccessButtonClick = useCallback(
    async (e: MouseEvent | KeyboardEvent): Promise<void> => {
      if (isRemoteActionsDisabled) return;
      e.preventDefault();
      setSTRequestAccessButtonLoading(true);

      if (!selectedMachineInfo?.machineId?.trim()) {
        setSTRequestAccessButtonLoading(false);
        return;
      }

      try {
        const res = await postVPNRequestSupportSessionUseCase({
          namespace: selectedMachineInfo?.namespace,
          machineId: selectedMachineInfo.machineId,
          gatewayId: selectedMachineInfo?.gatewayId,
          timeDuration: String(durationValue),
          timePeriod: durationType,
          requestMessage: requestMessage,
          visibility: sessionVisibility,
        });

        if (!res.success) {
          if (res?.error?.status === 409) {
            dispatch(
              updateModalsCloseDrawerModal({
                showModal: true,
                id: "machinesCancelRequestDisconnectSupportModal",
              }),
            );
          }
          errorSnackbar(res?.error?.data?.msg || res?.error?.message);
          return;
        }

        mergeSupportSessionResponse(res?.data);
        handleCloseModal();
      } finally {
        setSTRequestAccessButtonLoading(false);
      }
    },
    [
      dispatch,
      mergeSupportSessionResponse,
      selectedMachineInfo,
      durationValue,
      durationType,
      handleCloseModal,
      requestMessage,
      sessionVisibility,
      isRemoteActionsDisabled,
    ],
  );

  const handleOTRequestedAccessButtonClick = useCallback(
    async (e: MouseEvent | KeyboardEvent): Promise<void> => {
      if (isRemoteActionsDisabled) return;
      e.preventDefault();
      setOTRequestAccessButtonLoading(true);

      if (!selectedMachineInfo) {
        setOTRequestAccessButtonLoading(false);
        return;
      }

      try {
        const payload: postVPNAcceptSupportSessionDto = {
          namespace: selectedMachineInfo?.namespace,
          id: selectedMachineInfo?.id,
        };

        const requestedDuration = buildRequestedDuration({
          timeDuration: String(durationValue),
          timePeriod: durationType,
        });

        if (
          requestedDuration &&
          requestedDuration !== selectedMachineInfo?.requested_duration
        ) {
          payload.requestedDuration = requestedDuration;
        }

        const res = await postVPNAcceptSupportSessionUseCase(payload);

        if (!res.success) {
          if (res?.error?.status === 409) {
            dispatch(
              updateModalsCloseDrawerModal({
                showModal: true,
                id: "machinesCancelRequestDisconnectSupportModal",
              }),
            );
          }
          errorSnackbar(res?.error?.data?.msg || res?.error?.message);
          return;
        }
        mergeSupportSessionResponse(res?.data);
        handleCloseModal();
      } finally {
        setOTRequestAccessButtonLoading(false);
      }
    },
    [
      dispatch,
      mergeSupportSessionResponse,
      selectedMachineInfo,
      durationValue,
      durationType,
      handleCloseModal,
      isRemoteActionsDisabled,
    ],
  );

  const handleConfirmCancel = useCallback(
    async (
      e: MouseEvent | KeyboardEvent,
      userType: MachinesListSupportSessionsZitadelRoleTypes,
    ): Promise<void> => {
      if (isRemoteActionsDisabled) return;
      e.stopPropagation();
      setConfirmCancelLoading(true);

      if (!selectedMachineInfo) {
        handleCloseModal();
        setConfirmCancelLoading(false);
        return;
      }
      const payload = {
        namespace: selectedMachineInfo?.namespace,
        id: selectedMachineInfo?.id,
      };

      try {
        const res = await (userType ===
          MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN
          ? postVPNCancelSupportSessionUseCase
          : postVPNDenySupportSessionUseCase)(payload);

        if (!res.success) {
          errorSnackbar(res?.error);
          handleCloseModal();
          return;
        }

        mergeSupportSessionResponse(res?.data);
      } catch (error) {
        console.error("Error cancelling VPN request:", error);
      } finally {
        handleCloseModal();
        setConfirmCancelLoading(false);
      }
    },
    [
      mergeSupportSessionResponse,
      selectedMachineInfo,
      handleCloseModal,
      isRemoteActionsDisabled,
    ],
  );

  const requestedDurationIso = useMemo(() => {
    return decodeIso8601DurationToTimePeriod(
      selectedMachineInfo?.requested_duration,
    );
  }, [selectedMachineInfo?.requested_duration]);

  const conditionalStatusIcon = useMemo(() => {
    return hasSTPermission
      ? !IsRequestedStatus
        ? STRemoteSupportIcon
        : STRemoteSupportRequestedtIcon
      : OTRemoteSupportRequestedtIcon;
  }, [IsRequestedStatus, hasSTPermission]);

  const formattedStartedAt = useMemo(() => {
    return moment(selectedMachineInfo?.started_at).format(
      "MMMM D, YYYY h:mm A",
    );
  }, [selectedMachineInfo?.started_at]);

  return {
    requestMessage,
    durationValue,
    durationType,
    sessionVisibility,
    IsRequestedStatus,
    durationOptions,
    selectedMachineInfo,
    handleCloseModal,
    selectMinutes,
    selectHours,
    selectDurationValue,
    selectSessionVisibility,
    onChangeRequestMessage,
    handleSTRequestAccessButtonClick,
    handleOTRequestedAccessButtonClick,
    handleConfirmCancel,
    requestedDurationIso,
    conditionalStatusIcon,
    formattedStartedAt,
    hasSTPermission,
    isEditingDuration,
    handleEditDuration,
    handleCloseEditDuration,
    sTRequestAccessButtonLoading,
    oTRequestAccessButtonLoading,
    confirmCancelLoading,
  };
};
